import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { validateImage } from 'image-validator';
import { Avatar, Button, FileUploadButton } from 'components';
import { FailToastContainer } from 'components/Toast/ToastContainer';
import { appConfig } from 'config/app';
import { getAvatarUrlRequest, selectCurrentUser, updateCurrentUserRequest, uploadAvatarRequest } from 'store/User';
import { IAvatarUrlResponse, UserForm } from 'types';
import { AvatarActions, AvatarContainer, AvatarUploadFormProps } from './styles';

const AvatarUploadForm = ({ fluidWidth }: AvatarUploadFormProps) => {
  // Avatar
  const [avatarFields, setAvatarFields] = useState<IAvatarUrlResponse>();
  const [isLoadingAvatar, setIsLoadingAvatar] = useState<boolean>(false);

  // Data
  const dispatch = useDispatch();

  const { firstName, lastName, id: userId, avatarUrl } = useSelector(selectCurrentUser());

  const handleUploadClick = () => {
    dispatch(
      getAvatarUrlRequest((response) => {
        setAvatarFields(response);
      })
    );
  };

  const handleImageUpload = async (file: File) => {
    const isValidImage = await validateImage(file);

    if (!isValidImage) {
      toast(<FailToastContainer title="Error" message="The image format is not valid" />);
      return;
    }

    if (file.size > Math.floor(appConfig.AVATAR.MAX_FILE_SIZE)) {
      toast(<FailToastContainer title="Error" message={`The max image size is ${Math.floor(appConfig.AVATAR.MAX_FILE_SIZE / 1000000)}mb`} />);
      return;
    }

    if (avatarFields) {
      setIsLoadingAvatar(true);
      avatarFields.fields.file = file;

      dispatch(
        uploadAvatarRequest(avatarFields, () => {
          setIsLoadingAvatar(false);
        })
      );
    }
  };

  const handleRemoveAvatar = () => {
    setIsLoadingAvatar(true);
    dispatch(
      updateCurrentUserRequest(
        { userId, avatarUrl: '' },
        UserForm.SETTINGS,
        () => {
          setIsLoadingAvatar(false);
        },
        'Successfully removed avatar'
      )
    );
  };

  return (
    <AvatarContainer fluidWidth={fluidWidth} data-cy="account-settings-avatar-upload">
      <Avatar size="xlarge" username={`${firstName} ${lastName}`} image={avatarUrl} />

      <AvatarActions>
        <FileUploadButton
          label={avatarUrl ? 'Replace Image' : 'Upload Image'}
          size="small"
          onUpload={handleImageUpload}
          onClick={handleUploadClick}
          isLoading={isLoadingAvatar}
          disabled={isLoadingAvatar}
          accept="image/png, image/gif, image/jpeg"
        />
        {avatarUrl && <Button appearance="white" size="small" label="Remove" onClick={handleRemoveAvatar} disabled={isLoadingAvatar} />}
      </AvatarActions>
    </AvatarContainer>
  );
};

export default AvatarUploadForm;
